import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps';
import { useEffect, useState } from 'react';
import { Col, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { FaBuilding } from 'react-icons/fa';
import { FaArrowLeft } from 'react-icons/fa6';
import { RiAdminFill } from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as LedgerIcon } from '../../../../../assets/icons/ledger.svg';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { IconLink } from '../../../../../components/iconLink/IconLink';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../../../context';
import { User } from '../../../../../services/endpoints/people/user';
import { useGetLocationQuery } from '../../../../../services/endpoints/places/location';
import { getCurrentUser, LatLng } from '../../../../../services/helper';
import { formatResponse, SFCLocation } from '../../../../../types/place.types';
import { Venue } from '../../../../../types/venue.type';
import { canAccessFinancialReports, canEditLocation, canViewLocations } from '../../../../../utils/rbac';

// =============================================================================
// SHARED MARKER LABEL
// =============================================================================

const markerLabelStyle: React.CSSProperties = {
  background: 'white',
  padding: '5px',
  borderRadius: '5px',
  fontSize: '12px'
};

const MarkerLabel: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
    <div style={markerLabelStyle}>
      {icon}
      {children}
    </div>
);

// =============================================================================
// COMPONENT
// =============================================================================

const currentUser = getCurrentUser();

export const LocationDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const userHasAccess = canViewLocations(currentUser);
  const { data, error, isLoading } = useGetLocationQuery(+params.id!, {
    skip: !userHasAccess,
    refetchOnMountOrArgChange: true
  });
  const [location, setLocation] = useState<SFCLocation>();
  const [center, setCenter] = useState<LatLng>();
  const [showVenues, setShowVenues] = useState<boolean>(true);
  const [showAdmin, setShowAdmin] = useState<boolean>(true);
  const [showInstructor, setShowInstructor] = useState<boolean>(true);
  const googleApiKey = process.env.REACT_APP_GOOGLE_API_KEY || '';

  // Enhanced error handling
  useEffect(() => {
    if (error && !isLoading) {
      let errorMessage = 'Error while loading location';

      // Extract detailed error message
      if ('data' in error && typeof error.data === 'object' && error.data !== null) {
        const apiError = error.data as { message?: string };
        if (apiError.message) {
          errorMessage = apiError.message;

          // Handle specific error cases
          if (apiError.message.includes('may only view your own location')) {
            errorMessage = 'Access Denied: You can only view your own location.';
          } else if (apiError.message.includes('no assigned location')) {
            errorMessage = 'Your account is missing a location assignment. Please contact support.';
          }
        }
      } else if ('status' in error) {
        if (error.status === 403) {
          errorMessage = 'Access Denied: You do not have permission to view this location.';
        } else if (error.status === 404) {
          errorMessage = 'Location not found.';
        }
      }

      addToast(errorMessage, 'error');

      // Redirect to locations list after showing error
      setTimeout(() => navigate('/admin/places/locations'), 2000);
    }
  }, [data, error, isLoading, addToast, navigate]);

  useEffect(() => {
    if (data) {
      setLocation(formatResponse(data?.location));
      setCenter({
        lat: data?.location?.latitude,
        lng: data?.location?.longitude
      });
    }
  }, [data]);

  // Check permissions before rendering
  if (!userHasAccess) {
    return <Forbidden />;
  }

  if (isLoading || !location) {
    return (
        <div className="w-100 vh-100 d-inline-flex">
          <Spinner
              variant="primary"
              className="m-auto"
          />
        </div>
    );
  }

  const locationId = location.id;
  const showEditButton = canEditLocation(currentUser);
  const showFinancials = canAccessFinancialReports(currentUser, locationId);

  const userMarkerGroups = [
    { userTypeId: 4, color: 'blue',   visible: showAdmin },      // Admin
    { userTypeId: 5, color: 'green',  visible: showAdmin },      // Location Admin
    { userTypeId: 6, color: 'purple', visible: showInstructor },  // Instructor
  ];

  return (
      <>
        <div
            className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
            style={{ background: '#EFEFEF' }}
        >
          <Stack
              direction="horizontal"
              gap={2}
              className="ps-lg-3 ps-0"
          >
            <button
                className="btn btn-link text-info p-0"
                onClick={() => navigate('/admin/places/locations')}
            >
              <FaArrowLeft
                  size={20}
                  className="d-lg-none"
              />
              <FaArrowLeft
                  size={24}
                  className="d-none d-lg-block"
              />
            </button>
            <h2
                className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
                style={{ color: '#888888', fontSize: '1rem' }}
            >
              Locations List
            </h2>
            <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`${location?.title}`}</h2>
          </Stack>
          <Stack
              direction="horizontal"
              gap={1}
          >
            {showEditButton && (
                <SimpleButton
                    variant="primary"
                    height={36}
                    onClick={() => navigate(`/admin/places/locations/${location?.id}/edit`)}
                >
                  Edit location
                </SimpleButton>
            )}
          </Stack>
        </div>
        <div>
          <Stack
              gap={4}
              className="p-4"
          >
            <Row>
              <Col>
                <DetailSection title="Location Details">
                  <LabelText label="Business Entity Name:">
                    {location?.business_entity_name}
                  </LabelText>
                  <LabelText label="Business Phone Number:">{location?.phone_number}</LabelText>
                  <LabelText label="Primary City:">{location?.primary_city}</LabelText>
                  <LabelText label="Time Zone:">{location?.timezone ?? <em className="text-muted">Not set</em>}</LabelText>
                  <LabelText label="Area Director:">
                    <LinkCell
                        content={
                          location.manager?.first_name || location.manager?.last_name
                              ? `${location.manager?.first_name} ${location.manager?.last_name}`
                              : 'Empty'
                        }
                        url={`/admin/dash-users/admins/${location.manager?.id ?? ''}`}
                    />
                  </LabelText>
                  <LabelText label="Scheduling Email:">{location?.scheduling_email}</LabelText>
                  <LabelText label="Invoicing Email:">{location?.invoicing_email}</LabelText>
                  <LabelText label="Chef Hotline Number:">{location?.hotline}</LabelText>
                  <LabelText label="Tax ID Number:">{location?.tax_id_number}</LabelText>
                  <LabelText label="Remittance Mailing Addresses:">
                    {location?.addresses.length > 0 ? (
                        [location?.addresses[0]].map((a) => (
                            <>
                              {`${a.street}`}
                              {a.unit ? `, ${a.unit}` : ''}
                              <br />
                              {`${a.city}, ${a.state} ${a.zip_code}`}
                            </>
                        ))
                    ) : (
                        <></>
                    )}
                  </LabelText>
                </DetailSection>
              </Col>
              <Col>
                {showFinancials && (
                    <DetailSection title="Resources">
                      <LabelText label="Reports:"></LabelText>

                      <IconLink
                          icon={<LedgerIcon />}
                          text={'Location Financials'}
                          url={`/admin/places/locations/${location.id}/ledger`}
                      />

                      <IconLink
                          icon={<LedgerIcon />}
                          text={'Gross Revenue Weekly Reports'}
                          url={`/admin/places/locations/${location.id}/weekly-report`}
                      />
                    </DetailSection>
                )}
              </Col>
            </Row>
            <APIProvider apiKey={googleApiKey}>
              <div style={{ width: '100%', height: '500px' }}>
                <Form>
                  <div className="mb-3">
                    <Form.Check
                        inline
                        label="Venues"
                        name="group1"
                        type="checkbox"
                        checked={showVenues}
                        onChange={() => setShowVenues(!showVenues)}
                    />

                    <Form.Check
                        inline
                        label="Admin"
                        name="group1"
                        type="checkbox"
                        checked={showAdmin}
                        onChange={() => setShowAdmin(!showAdmin)}
                    />

                    <Form.Check
                        inline
                        label="Instructor"
                        name="group1"
                        type="checkbox"
                        checked={showInstructor}
                        onChange={() => setShowInstructor(!showInstructor)}
                    />
                  </div>
                </Form>
                <Map
                    mapId="7914a7f3b7361cce"
                    defaultCenter={center}
                    defaultZoom={12}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    onClick={(e) => {
                      if (e.detail.latLng) {
                        setCenter({
                          lat: e.detail.latLng.lat,
                          lng: e.detail.latLng.lng
                        });
                      }
                    }}
                >
                  {/* Venue markers */}
                  {showVenues &&
                      location?.venues.length > 0 &&
                      location?.venues?.map((marker: Venue) => (
                          <AdvancedMarker
                              key={marker.id}
                              position={{
                                lat: marker?.latitude || 0,
                                lng: marker?.longitude || 0
                              }}
                          >
                            <MarkerLabel icon={<FaBuilding size={30} color="red" />}>
                              {marker?.title}
                            </MarkerLabel>
                          </AdvancedMarker>
                      ))}

                  {/* User markers (Admin, Location Admin, Instructor) */}
                  {userMarkerGroups.map(({ userTypeId, color, visible }) =>
                      visible &&
                      location?.users.length > 0 &&
                      location?.users
                          .filter((user: User) => user?.user_type_id === userTypeId)
                          .map((user: User) => (
                              <AdvancedMarker
                                  key={user.id}
                                  position={{
                                    lat: Number(user?.latitude) || 0,
                                    lng: Number(user?.longitude) || 0
                                  }}
                              >
                                <MarkerLabel icon={<RiAdminFill size={30} color={color} />}>
                                  {user?.username}
                                </MarkerLabel>
                              </AdvancedMarker>
                          ))
                  )}
                </Map>
              </div>
            </APIProvider>
          </Stack>
        </div>
      </>
  );
};